import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import { CheckCircleOutlineRounded, Visibility, VisibilityOff } from "@mui/icons-material";
import AuthLayout, { authStyles as styles } from "../../components/customComponents/authLayout/AuthLayout";
import { authApi } from "../../auth/authApi";
import { authErrorMessage, validatePassword } from "../../utils/authSession";

/**
 * Landing page for the set-password link an admin invitation emails out.
 *
 * The link is expected to be single-use and time-limited. Both "already used" and "expired" are
 * expected to come back as 410 with TOKEN_ALREADY_USED / TOKEN_EXPIRED, and the behaviour for
 * both is the same: explain, then offer a way to the sign-in page - never render a password form
 * that cannot possibly succeed.
 *
 * Setting the password is what is expected to mark the invitation accepted server-side.
 */
export default function SetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState("checking"); // "checking" | "valid" | "invalid" | "done"
  const [account, setAccount] = useState({ email: "", firstName: "" });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const checkToken = useCallback(async () => {
    if (!token) {
      setStatus("invalid");
      setError("This link is missing its token.");
      return;
    }
    try {
      const res = await authApi.getPasswordSetup(token);
      const data = res?.data || {};
      setAccount({ email: data.email || "", firstName: data.firstName || "" });
      setStatus("valid");
    } catch (err) {
      console.error("Password setup token check failed:", err);
      setError(
        authErrorMessage(
          err,
          "This set-password link is no longer valid. Ask your administrator to send a new invitation.",
        ),
      );
      setStatus("invalid");
    }
  }, [token]);

  useEffect(() => {
    checkToken();
  }, [checkToken]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const problem = validatePassword(password, confirmPassword);
    if (problem) {
      setError(problem);
      return;
    }

    setError("");
    setSubmitting(true);
    try {
      await authApi.setupPassword({ token, password, confirmPassword });
      setStatus("done");
    } catch (err) {
      console.error("Password setup failed:", err);
      setError(authErrorMessage(err, "We couldn't set your password."));
      setSubmitting(false);
    }
  };

  const greeting = account.firstName ? `Welcome, ${account.firstName}` : "Set your password";

  return (
    <AuthLayout
      heading={status === "done" ? "You're all set" : greeting}
      subheading={
        status === "valid"
          ? `Choose a password for ${account.email}. At least 8 characters, with one letter and one digit.`
          : undefined
      }
    >
      {status === "checking" && (
        <div className={styles.spinnerRow}>
          <CircularProgress size={26} color="inherit" aria-label="Checking your link" />
        </div>
      )}

      {status === "invalid" && (
        <div className={styles.form}>
          <Alert severity="error" className={styles.alert}>
            {error}
          </Alert>
          <button type="button" className={styles.primaryButton} onClick={() => navigate("/", { replace: true })}>
            Go to sign in
          </button>
        </div>
      )}

      {status === "valid" && (
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.fieldGroup}>
            <label className={styles.visuallyHidden} htmlFor="setup-password">
              Password
            </label>
            <input
              id="setup-password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              className={styles.inputField}
              value={password}
              onChange={(e) => {
                setError("");
                setPassword(e.target.value);
              }}
              autoComplete="new-password"
              disabled={submitting}
              autoFocus
            />
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setShowPassword((prev) => !prev)}
              onMouseDown={(e) => e.preventDefault()}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </button>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.visuallyHidden} htmlFor="setup-confirm-password">
              Confirm password
            </label>
            <input
              id="setup-confirm-password"
              type={showPassword ? "text" : "password"}
              placeholder="Confirm password"
              className={styles.inputField}
              value={confirmPassword}
              onChange={(e) => {
                setError("");
                setConfirmPassword(e.target.value);
              }}
              autoComplete="new-password"
              disabled={submitting}
            />
          </div>

          {error && (
            <Alert severity="error" className={styles.alert}>
              {error}
            </Alert>
          )}

          <button
            type="submit"
            className={styles.primaryButton}
            disabled={!password || !confirmPassword || submitting}
          >
            {submitting ? "Saving…" : "Set password"}
          </button>
        </form>
      )}

      {status === "done" && (
        <div className={styles.form}>
          <Alert severity="success" icon={<CheckCircleOutlineRounded fontSize="small" />} className={styles.alert}>
            Your password is set. You can sign in with it now.
          </Alert>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => navigate("/login/password", { replace: true })}
          >
            Sign in
          </button>
        </div>
      )}
    </AuthLayout>
  );
}
